import { ChevronRight } from 'react-feather';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetMessageQuery as GetMessage,
  GetMessageRecipientsQuery as GetMessageRecipients,
} from '__generated__/graphql';

import { encodeUrl } from '../../../common/helpers';
import SendMessageForm from './SendMessageForm';
import UI from '../../../common/UI';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';

type Message = GetMessage['project']['message'];
type Recipients = GetMessageRecipients['project']['message'];

export interface SendMessagePageProps {
  message: Message;
  recipients?: Recipients;
}

const SendMessagePage = ({ message, recipients }: SendMessagePageProps) => {
  const { t } = useTranslation();
  const { formatInteger, formatDate, parseDate } = useLocale();
  const project = useProject();

  const [messageSent, setMessageSent] = useState(false);
  const sendingRef = useRef(false);

  useEffect(() => {
    if (message) {
      if (message.remaining_count > 0) {
        sendingRef.current = true;
      } if (sendingRef.current) {
        setMessageSent(true);
        sendingRef.current = false;
      }
    }

    return undefined;
  }, [message]);

  if (!recipients) {
    return <UI.PageLoader />;
  }

  return (
    <>
      <UI.Div sc={{ mb: 4 }}>
        <UI.SectionHeading sc={{ mb: 0 }}>
          {t('send_message')}
        </UI.SectionHeading>
        <UI.InputDescription>
          {t('send_message_description')}
        </UI.InputDescription>
      </UI.Div>
      <UI.Card sc={{ hasPanels: true }}>
        <UI.GridContainer
          sc={{ columns: '1fr fit-content(200px)', alignVertical: ['start', 'center'] }}
          style={{ minHeight: 40 }}
        >
          <UI.FlexContainer sc={{ wrap: 'wrap' }}>
            {message.remaining_count === 0 && (
              <UI.Div sc={{ mr: 4, mb: [4, 0], width: ['100%', 'auto'] }}>
                <UI.LinkBlock
                  to={`/${project.id}/participants/contacts?${encodeUrl({
                    event: message.event.id,
                    will_receive_messages: [message.id],
                  })}`}
                  title={t('recipients', { count: recipients.next_recipient_count })}
                >
                  <UI.Span sc={{ block: true, fontSize: 2, fontWeight: 500 }}>
                    {formatInteger(recipients.next_recipient_count)}
                  </UI.Span>
                  <UI.Span sc={{ block: true, noWrap: true, small: true }}>
                    {t('recipients', { count: recipients.next_recipient_count })}
                    {' '}
                    <UI.Icon sc={{ muted: true }}>
                      <ChevronRight />
                    </UI.Icon>
                  </UI.Span>
                </UI.LinkBlock>
              </UI.Div>
            )}
            {message.remaining_count > 0 && (
              <UI.Div sc={{ mr: 4, mb: [4, 0], width: ['100%', 'auto'] }}>
                <UI.Span sc={{ block: true, fontSize: 2, fontWeight: 500 }}>
                  {formatInteger(message.remaining_count)}
                </UI.Span>
                <UI.Span sc={{ block: true, noWrap: true, small: true }}>
                  {t('remaining', { count: message.remaining_count })}
                </UI.Span>
              </UI.Div>
            )}
            {(message.remaining_count > 0 || recipients.previous_recipient_count > 0) && (
              <UI.Div sc={{ mr: 4, mb: [4, 0], width: ['100%', 'auto'] }}>
                <UI.LinkBlock
                  to={`/${project.id}/participants/contacts?${encodeUrl({
                    event: message.event.id,
                    received_messages: [message.id],
                  })}`}
                  title={t('previous_recipients', { count: recipients.previous_recipient_count })}
                >
                  <UI.Span sc={{ block: true, fontSize: 2, fontWeight: 500 }}>
                    {formatInteger(recipients.previous_recipient_count)}
                  </UI.Span>
                  <UI.Span sc={{ block: true, noWrap: true, small: true }}>
                    {t('previous_recipients', { count: recipients.previous_recipient_count })}
                    {' '}
                    <UI.Icon sc={{ muted: true }}>
                      <ChevronRight />
                    </UI.Icon>
                  </UI.Span>
                </UI.LinkBlock>
              </UI.Div>
            )}
            {message.last_sent_at && (
              <UI.Div>
                <UI.Span sc={{ block: true, fontSize: 2, fontWeight: 500, noWrap: true }}>
                  {formatDate(
                    parseDate(message.last_sent_at, { timezone: 'UTC' }),
                    { format: 'display_compact_date_time' },
                  )}
                </UI.Span>
                <UI.Span sc={{ block: true, noWrap: true, small: true }}>
                  {t('last_sent')}
                </UI.Span>
              </UI.Div>
            )}
          </UI.FlexContainer>
        </UI.GridContainer>

        {message.remaining_count > 0 && (
          <UI.Div>
            <UI.Info icon={<UI.Loader />}>
              <UI.H4>
                {t('message_being_sent')}
              </UI.H4>
              {t('message_being_sent_description')}
            </UI.Info>
          </UI.Div>
        )}

        {message.remaining_count === 0 && (
          <SendMessageForm message={message} recipients={recipients} messageSent={messageSent} />
        )}
      </UI.Card>
    </>
  );
};

export default SendMessagePage;
